/**
 * assets/msd-kit-files.js
 * Fills every schematic sheet (snippets/msd-kit-sheet.liquid) from /api/kit-files.
 *
 * Buyers' sheets carry the proof from snippets/msd-kit-access.liquid. Everyone makes
 * the same call: the server decides what comes back. All published revisions arrive
 * in one response, so choosing a revision re-renders from memory. Premium links
 * expire after 24 hours, so nothing from the response is stored.
 */
(function () {
  'use strict';

  var locale = document.documentElement.lang || 'en';

  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function fill(template, values) {
    return template.replace(/\{(\w+)\}/g, function (match, key) {
      return key in values ? values[key] : match;
    });
  }

  function storedRevision(handle) {
    try {
      return window.localStorage.getItem('msd-kit-rev:' + handle);
    } catch (err) {
      return null;
    }
  }

  function storeRevision(handle, id) {
    try {
      window.localStorage.setItem('msd-kit-rev:' + handle, String(id));
    } catch (err) {
      /* Private window or blocked storage: the choice just isn't remembered. */
    }
  }

  function parseDate(value) {
    return value ? Date.parse(String(value).replace(' ', 'T') + 'Z') : NaN;
  }

  function monthYear(value) {
    var time = parseDate(value);
    if (isNaN(time)) return '';
    return new Date(time).toLocaleDateString(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }

  function fileSize(bytes) {
    if (!bytes) return '';
    var megabytes = bytes / (1024 * 1024);
    if (megabytes >= 1) return megabytes.toLocaleString(locale, { maximumFractionDigits: 1 }) + ' MB';
    return Math.max(1, Math.round(bytes / 1024)).toLocaleString(locale) + ' KB';
  }

  /** Older responses have no revisions: treat the top-level fields as the only one. */
  function revisionsOf(data) {
    if (data.revisions && data.revisions.length) return data.revisions;
    return [{
      id: null,
      is_current: true,
      open_files: data.open_files || [],
      protected_files: data.protected_files || [],
      protected_count: data.protected_count || 0
    }];
  }

  function hasFiles(revisions) {
    return revisions.some(function (revision) {
      return (revision.open_files || []).length
        || (revision.protected_files || []).length
        || revision.protected_count;
    });
  }

  /**
   * A stored choice wins, then the revision current when they bought, then the latest.
   * Rev A's date is when its files were first uploaded, so orders can be older than
   * every revision. Those buyers have the oldest board, not the newest.
   */
  function defaultRevision(root, revisions) {
    var stored = storedRevision(root.dataset.kit);
    var chosen = revisions.filter(function (revision) { return String(revision.id) === stored; })[0];
    if (chosen) return chosen;

    var boughtAt = Number(root.dataset.boughtAt) * 1000;
    if (boughtAt) {
      var atPurchase = revisions.filter(function (revision) {
        return parseDate(revision.published_at) <= boughtAt;
      })[0];
      return atPurchase || revisions[revisions.length - 1];
    }
    return revisions[0];
  }

  function fileRow(file, strings) {
    var row = make('li', 'msd-sheet__file');
    var link = make('a', 'msd-sheet__link');
    link.href = file.download_url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    link.appendChild(make('span', 'msd-sheet__name', file.display_name || 'File'));
    var meta = [String(file.file_type || '').toUpperCase(), fileSize(file.size)].filter(Boolean).join('  ');
    link.appendChild(make('span', 'msd-sheet__meta', meta));
    link.appendChild(make('span', 'msd-sheet__get', strings.download));

    row.appendChild(link);
    return row;
  }

  function fileList(files, strings) {
    var list = make('ul', 'msd-sheet__files');
    files.forEach(function (file) { list.appendChild(fileRow(file, strings)); });
    return list;
  }

  function groupHead(label) {
    var head = make('div', 'msd-sheet__group-head');
    var title = make('div', 'msd-sheet__label', label);
    title.setAttribute('role', 'heading');
    title.setAttribute('aria-level', '3');
    head.appendChild(title);
    return head;
  }

  function jumper(text) {
    var wrap = make('span', 'msd-sheet__j1');
    var part = make('span', 'msd-sheet__jumper');
    part.setAttribute('aria-hidden', 'true');
    wrap.appendChild(part);
    wrap.appendChild(make('span', '', text));
    return wrap;
  }

  function lockedBody(count, root, strings) {
    var body = make('div', 'msd-sheet__locked');
    var pins = make('ul', 'msd-sheet__pins');
    pins.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < Math.min(count, 8); i++) pins.appendChild(make('li', 'msd-sheet__pin'));
    body.appendChild(pins);

    var kind = root.dataset.lockedKind === 'bundle' ? 'locked_bundle_' : 'locked_kit_';
    var template = strings[kind + (count === 1 ? 'one' : 'other')];
    body.appendChild(make('div', 'msd-sheet__locked-text', fill(template, { count: count })));
    return body;
  }

  function renderFiles(state, revision) {
    var box = state.files;
    var strings = state.strings;
    var open = revision.open_files || [];
    var premium = revision.protected_files || [];
    var lockedCount = state.authorized ? 0 : (revision.protected_count || 0);

    box.textContent = '';

    if (open.length) {
      var free = make('section', 'msd-sheet__group');
      free.appendChild(groupHead(strings.free));
      free.appendChild(fileList(open, strings));
      box.appendChild(free);
    }

    if (state.authorized && premium.length) {
      // Starts open so the first render shows J1 closing, once per page load.
      var unlocked = make('section', 'msd-sheet__group' + (state.closed ? ' msd-sheet__group--live' : ''));
      var liveHead = groupHead(strings.premium);
      liveHead.appendChild(jumper(strings.jumper_closed));
      unlocked.appendChild(liveHead);
      unlocked.appendChild(fileList(premium, strings));
      box.appendChild(unlocked);

      if (!state.closed) {
        state.closed = true;
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () { unlocked.classList.add('msd-sheet__group--live'); });
        });
      }
    } else if (lockedCount) {
      var locked = make('section', 'msd-sheet__group');
      var lockedHead = groupHead(strings.premium);
      lockedHead.appendChild(jumper(strings.jumper_open));
      locked.appendChild(lockedHead);
      locked.appendChild(lockedBody(lockedCount, state.root, strings));
      box.appendChild(locked);
    }
  }

  function renderNotes(state, revision) {
    var box = state.notes;
    var strings = state.strings;
    var latest = state.revisions[0];
    box.textContent = '';

    function note(className, heading, text) {
      var block = make('div', 'msd-sheet__note' + (className ? ' ' + className : ''));
      if (heading) block.appendChild(make('div', 'msd-sheet__note-head', heading));
      if (text) block.appendChild(make('div', 'msd-sheet__note-text', text));
      box.appendChild(block);
      return block;
    }

    if (revision.warning) note('msd-sheet__note--warning', strings.known_issue, revision.warning);
    if (revision.notes) note('', strings.notes, revision.notes);

    if (revision !== latest) {
      var older = note('', '', fill(strings.older, { label: revision.label, latest: latest.label }));
      var button = make('button', 'msd-sheet__switch', strings.show_latest);
      button.type = 'button';
      button.addEventListener('click', function () { choose(state, latest, true); });
      older.appendChild(button);
    }

    box.hidden = !box.childNodes.length;
  }

  function choose(state, revision, remember) {
    state.selected = revision;
    if (state.select) state.select.value = String(revision.id);
    if (remember) storeRevision(state.root.dataset.kit, revision.id);

    var issued = monthYear(revision.published_at);
    state.issuedCell.hidden = !issued;
    state.issued.textContent = issued;

    renderFiles(state, revision);
    renderNotes(state, revision);
  }

  function setUpSelect(state) {
    var revisions = state.revisions;
    if (revisions.length < 2) return;

    var select = state.root.querySelector('[data-msd-rev-select]');
    revisions.forEach(function (revision) {
      var date = monthYear(revision.published_at);
      var text = revision.label + (date ? ', ' + date : '');
      if (revision === revisions[0]) text += ' (' + state.strings.latest + ')';
      var option = make('option', '', text);
      option.value = String(revision.id);
      select.appendChild(option);
    });
    select.addEventListener('change', function () {
      var picked = revisions.filter(function (revision) { return String(revision.id) === select.value; })[0];
      if (picked) choose(state, picked, true);
    });

    state.select = select;
    state.root.querySelector('[data-msd-rev-cell]').hidden = false;
    state.root.classList.add('has-revisions');
  }

  function hideEmptySheet(root) {
    var section = root.closest('[data-msd-kit-section]');
    var alone = section && section.querySelectorAll('[data-msd-kit]').length === 1;
    (alone ? section : root).hidden = true;
  }

  function apiUrl(root) {
    return root.dataset.serverUrl.replace(/\/$/, '')
      + '/api/kit-files/' + encodeURIComponent(root.dataset.kit)
      + '?customer_id=' + encodeURIComponent(root.dataset.customerId || '')
      + '&ts=' + encodeURIComponent(root.dataset.accessTs || '')
      + '&sig=' + encodeURIComponent(root.dataset.accessSig || '');
  }

  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = '1';

    var strings = JSON.parse(root.querySelector('[data-msd-kit-strings]').textContent);
    var status = root.querySelector('[data-msd-kit-status]');

    if (!root.dataset.serverUrl) {
      status.hidden = true;
      console.warn('[msd-kit-files] No server URL configured in section settings.');
      return;
    }

    fetch(apiUrl(root))
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var revisions = revisionsOf(data);
        if (!hasFiles(revisions)) {
          hideEmptySheet(root);
          return;
        }

        var state = {
          root: root,
          strings: strings,
          revisions: revisions,
          authorized: Boolean(data.authorized),
          closed: false,
          select: null,
          files: root.querySelector('[data-msd-kit-files]'),
          notes: root.querySelector('[data-msd-kit-notes]'),
          issued: root.querySelector('[data-msd-issued]'),
          issuedCell: root.querySelector('[data-msd-issued-cell]')
        };

        var intro = root.querySelector('[data-msd-kit-intro]');
        if (intro && state.authorized) intro.textContent = strings.intro_buyer;

        setUpSelect(state);
        status.hidden = true;
        state.files.hidden = false;
        choose(state, defaultRevision(root, revisions), false);
      })
      .catch(function (err) {
        status.textContent = strings.error;
        console.error('[msd-kit-files] ' + root.dataset.kit, err);
      });
  }

  function initAll(scope) {
    scope.querySelectorAll('[data-msd-kit]').forEach(init);
  }

  initAll(document);
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
}());
